import { Button, Flex, Switch, Text } from '@chakra-ui/react';
import { interactionSubmissionDownload, interactionSubmissionToggle } from 'api/interaction';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { INTERACTION_SUBMISSION } from 'constants/mixpanel';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useAppSelector } from 'modules/Editor/store';
import { selectOriginalEditorId } from 'modules/Editor/store/editorSettingsSelector';
import { selectInfographId } from 'modules/Editor/store/infographSelector';
import { useToast } from 'modules/common/components/Toast';
import { useCallback, useState } from 'react';
import { useLoaderData } from 'react-router-dom';

import { saveBlobAs } from './InteractionSubmission.helpers';

const DEFAULT_DOWNLOAD_FORMAT = 'csv';
const DOWNLOAD_BUTTON_TEST_ID = 'download-interaction-submission-button';

export const InteractionSubmission = () => {
  const { venngageE2 } = useVenngageE1Gate();
  const token = useLoaderData() as string;
  const infographId = useAppSelector(selectInfographId);
  const originalEditorId = useAppSelector(selectOriginalEditorId);
  const [enabled, setEnabled] = useState(!!venngageE2?.enabledInteractionSubmission);
  const [isDownloading, setIsDownloading] = useState(false);
  const toast = useToast();

  const onToggleEnabledSubmission = useCallback(async () => {
    try {
      if (!originalEditorId) return;

      const isEnabled = !enabled;
      await interactionSubmissionToggle({ originalEditorId, enabled: isEnabled });
      setEnabled(isEnabled);

      Mixpanel.track(INTERACTION_SUBMISSION.Enabled.eventName, {
        enabled: isEnabled,
        designId: originalEditorId,
      });
    } catch (error) {
      toast({
        title: 'Failed to update permission',
        status: 'error',
      });
    }
  }, [originalEditorId, enabled, toast]);

  const handleDownload = useCallback(async () => {
    try {
      if (!infographId || !token) return;

      setIsDownloading(true);
      const format = DEFAULT_DOWNLOAD_FORMAT;
      const csvBlob = await interactionSubmissionDownload({ id: infographId, format }, token);

      saveBlobAs(csvBlob, `data.${format}`);

      Mixpanel.track(INTERACTION_SUBMISSION.Download.eventName, {
        designId: originalEditorId,
        format,
      });
    } catch (error) {
      toast({
        title: 'Download failed',
        status: 'error',
      });
    } finally {
      setIsDownloading(false);
    }
  }, [originalEditorId, infographId, token, toast]);

  return (
    <Flex flexDir='column' gap={2} w='full'>
      <Flex>
        <Text fontSize='sm' fontWeight='semibold' color='font.500'>
          Options
        </Text>
      </Flex>
      <Flex flexDir='row' justifyContent='space-between' alignItems='center'>
        <Text fontSize='xs' color='font.300'>
          Share as fillable form
        </Text>
        <Switch isChecked={enabled} onChange={onToggleEnabledSubmission} />
      </Flex>
      <Flex flexDir='row' justifyContent='space-between' alignItems='center'>
        <Text fontSize='xs' color='font.300' maxW='200px'>
          Download all submissions data in CSV format
        </Text>
        <Button
          isDisabled={isDownloading}
          isLoading={isDownloading}
          onClick={handleDownload}
          variant='outline'
          aria-label={'Download all submissions data'}
          size='sm'
          width='28'
          leftIcon={<DownloadIcon style={{ marginRight: 1 }} />}
          data-testid={DOWNLOAD_BUTTON_TEST_ID}
        >
          <Text>Download</Text>
        </Button>
      </Flex>
    </Flex>
  );
};
